import React, { ReactNode, useEffect, useMemo } from 'react'
import useAxios from 'axios-hooks'
import { useParams } from 'react-router'
import styled from '@emotion/styled'
import { IonButton, IonIcon } from '@ionic/react'
import { chevronDownOutline, downloadOutline } from 'ionicons/icons'

import aplicacao_padrao from '../assets/marca/aplicacao_padrao.png'
import { useWindowSize } from '../common/hooks/useWindowSize'
import { CertificadoStore } from './CertificadoStore'
import { endOfDay, format } from 'date-fns'
import { ReactComponent as Logo } from '../assets/matricula/logo.svg'
import { keyframes } from '@emotion/react'
import { MatriculaModel } from '../models/MatriculaModel'

const DadosAlunos_name = 'DadosAlunos'
const IframePdf_name = 'IframePdf'
const InformacaoAdicionais_name = 'InformacaoAdicionais'
const DadosCurso_name = 'DadosCurso'
const DadosEmissora_name = 'DadosEmissora'

export function Certificado() {
  const { Layout, Container } = Certificado
  return (
    <CertificadoStore.Provider>
      <Loading />
      <StoreShow when={({ loading, errorReason }) => !loading && !!errorReason}>
        <Header />
        <ErrorContainer />
      </StoreShow>
      <StoreShow when={({ errorReason }) => !errorReason}>
        <Layout id="Layout">
          <Header />
          <Container>
            <DadosAlunos />
            <IframePdf />
            <InformacaoAdicionais />
          </Container>
        </Layout>
      </StoreShow>
    </CertificadoStore.Provider>
  )
}
Certificado.Layout = styled.div`
  height: 100%;
  overflow-y: scroll;
`
Certificado.Container = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 100%;
  grid-template-areas:
    '${DadosAlunos_name} ${DadosAlunos_name}'
    '${IframePdf_name} ${InformacaoAdicionais_name}';

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 80% auto;
    grid-template-areas:
      '${DadosAlunos_name}'
      '${IframePdf_name}'
      '${InformacaoAdicionais_name}';
  }
`

function StoreShow({
  children,
  when,
}: {
  children: ReactNode
  when: (state: ReturnType<typeof CertificadoStore.useState>) => boolean
}) {
  const state = CertificadoStore.useState()
  return when(state) ? <>{children}</> : null
}

function ErrorContainer() {
  const { errorReason } = CertificadoStore.useState()
  const { Container, Box } = ErrorContainer
  return (
    <Container>
      <h2>Não foi possível exibir o certificado</h2>
      <div style={{ marginTop: 24, textAlign: 'left' }}>
        Motivo do erro
        <Box>{errorReason}</Box>
      </div>
    </Container>
  )
}
ErrorContainer.Container = styled.div`
  height: 100vh;
  display: grid;
  align-content: center;
  width: 400px;
  margin: 0 auto;
`
ErrorContainer.Box = styled.div`
  margin-top: 8px;
  border-radius: 8px;
  padding: 24px;
  background: #FF642254;
  text-align: center;
`

function Loading() {
  const { Container } = Loading
  const { loading } = CertificadoStore.useState()

  if (!loading) return null
  return (
    <Container>
      <Logo />
      Carregando certificado...
    </Container>
  )
}
const animateLoading = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);  /* You can adjust scale factor */
  }
  100% {
    transform: scale(1);
  }
`
Loading.Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 12px;
  color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: white;
  z-index: 1000;
  svg {
    animation: ${animateLoading} 5s ease-in-out infinite;
    height: auto;
    width: 400px;
    max-width: 80vw;
  }
`

function Header() {
  const { Cabecalho, Logo } = Header
  return (
    <Cabecalho>
      <Logo src={aplicacao_padrao} />
    </Cabecalho>
  )
}
Header.Cabecalho = styled.div`
  height: 80px;
  background-color: #FF6422;
`
Header.Logo = styled.img`
  object-fit: contain;
  height: 100%;
  background-color: #fff;
  padding: 10px;
`

function DescriptionList({ children, columns = 1 }: { children: ReactNode; columns?: number }) {
  const { Container } = DescriptionList
  return <Container columns={columns}>{children}</Container>
}
DescriptionList.Container = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, minmax(0, 1fr));
  gap: 12px 20px;
`

function DescriptionItem({
  label,
  children,
  fullWidth = false,
}: {
  label: ReactNode
  children: ReactNode
  fullWidth?: boolean
}) {
  const { Container, Label, Value } = DescriptionItem
  return (
    <Container fullWidth={fullWidth}>
      <Label>{label}</Label>
      <Value>{children}</Value>
    </Container>
  )
}
DescriptionItem.Container = styled.div<{ fullWidth: boolean }>`
  display: grid;
  gap: 4px;
  min-width: 0;
  ${(props) => (props.fullWidth ? 'grid-column: 1 / -1;' : '')}
`
DescriptionItem.Label = styled.div`
  font-size: 14px;
  line-height: 18px;
  color: #5f5f5f;
`
DescriptionItem.Value = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #2d2d2d;
`

function AccordionSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: ReactNode
  children: ReactNode
  defaultOpen?: boolean
}) {
  const { Layout, Header, Icon, Content } = AccordionSection
  return (
    <Layout open={defaultOpen}>
      <Header>
        {title}
        <Icon className="accordion-icon" icon={chevronDownOutline} />
      </Header>
      <Content>{children}</Content>
    </Layout>
  )
}
AccordionSection.Layout = styled.details`
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;

  &[open] .accordion-icon {
    transform: rotate(180deg);
  }
`
AccordionSection.Header = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  list-style: none;
  padding: 12px 14px;
  background-color: #fafafa;

  &::-webkit-details-marker {
    display: none;
  }
`
AccordionSection.Icon = styled(IonIcon)`
  font-size: 18px;
  color: #666;
  transition: transform 0.2s ease;
`
AccordionSection.Content = styled.div`
  padding: 12px 14px;
`

function DadosAlunos() {
  const { width } = useWindowSize()
  const { StatusCertificado, Container } = DadosAlunos
  const { matricula } = CertificadoStore.useState()
  const statusCertificado = matricula.dataValidadeCurso
    ? endOfDay(new Date(matricula.dataValidadeCurso)) > new Date()
    : true
  const descriptionColumns = width && width < 768 ? 1 : 2

  return (
    <Container>
      <DescriptionList columns={descriptionColumns}>
        <DescriptionItem label={<strong>Nome</strong>}>
          {matricula?.aluno?.nome.toUpperCase()}
        </DescriptionItem>
        <DescriptionItem label={<strong>CPF</strong>}>{matricula?.aluno?.cpf}</DescriptionItem>
        <DescriptionItem label={<strong>Status do certificado</strong>}>
          <StatusCertificado statusCertificado={statusCertificado}>
            {statusCertificado ? 'Ativo' : 'Vencido'}
          </StatusCertificado>
        </DescriptionItem>
      </DescriptionList>
    </Container>
  )
}
DadosAlunos.Container = styled.div`
  margin-left: 10px;
  grid-area: ${DadosAlunos_name};
`
type StatusCertificadoProps = {
  statusCertificado: boolean
}
DadosAlunos.StatusCertificado = styled.div<StatusCertificadoProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => (props.statusCertificado ? '#c3ee66' : 'red')};
  border-radius: 0.313rem;
  padding: 0.5rem 0.75rem;
  width: 50%;
  font-weight: bold;
`

function IframePdf() {
  const { Container } = IframePdf
  const dispatch = CertificadoStore.useDispatch()
  const { pdf } = CertificadoStore.useState()
  const { certificadoId } = useParams<{ certificadoId: string }>()
  const size = { height: '100%', width: '98%' }
  useEffect(() => {
    if (!certificadoId) return
    dispatch(CertificadoStore.thunks.getCertificadoValidacao(certificadoId))

    return () => {
      URL.revokeObjectURL(pdf)
    }
  }, [certificadoId])

  return (
    <Container style={{ ...size }}>
      <iframe id="iframe-pdf" src={pdf} title="Visualização" allow="fullscreen" style={{ ...size }}>
        O seu certificado será exibido em breve...
      </iframe>
    </Container>
  )
}
IframePdf.Container = styled.div`
  grid-area: ${IframePdf_name};
  margin-left: 10px;
`

function InformacaoAdicionais() {
  const { Container } = InformacaoAdicionais
  return (
    <Container>
      <BotaoDownload />
      <AccordionSection title={<strong>Dados do Curso</strong>} defaultOpen>
        <DadosCurso />
      </AccordionSection>
      <AccordionSection title={<strong>Dados da Emissora</strong>}>
        <DadosEmissora />
      </AccordionSection>
    </Container>
  )
}
InformacaoAdicionais.Container = styled.div`
  grid-area: ${InformacaoAdicionais_name};
  width: 99%;
  padding: 5px;
  display: grid;
  align-content: start;
  gap: 10px;
`

function BotaoDownload() {
  const { pdf } = CertificadoStore.useState()
  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = pdf
    link.download = 'certificado.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <IonButton
      shape="round"
      onClick={handleDownload}
      style={{
        '--background': '#FF6422',
        '--background-hover': '#eb8920',
        '--color': '#ffffff',
        '--border-radius': '999px',
        '--padding-start': '14px',
        '--padding-end': '14px',
        margin: 0,
        width: 'fit-content',
      }}
    >
      <IonIcon slot="start" icon={downloadOutline} style={{ fontSize: 20 }} />
      <strong> BAIXAR PDF</strong>
    </IonButton>
  )
}

function DadosEmissora() {
  const { Container, Titulo } = DadosEmissora
  return (
    <Container>
      <DescriptionList>
        <DescriptionItem label={<strong>Nome</strong>}>New Driver</DescriptionItem>
        <DescriptionItem label={<strong>CNPJ</strong>}>31.665.008.0001/09</DescriptionItem>
      </DescriptionList>
      <Titulo>
        <strong>Endereço</strong>
      </Titulo>
      <DescriptionList>
        <DescriptionItem label={<strong>Logradouro</strong>}>R. C-139</DescriptionItem>
        <DescriptionItem label={<strong>Cidade</strong>}>Goiânia - GO</DescriptionItem>
        <DescriptionItem label={<strong>CEP</strong>}>74275-070</DescriptionItem>
      </DescriptionList>
    </Container>
  )
}
DadosEmissora.Container = styled.div`
  grid-area: ${DadosEmissora_name};
  display: grid;
  gap: 12px;
`
DadosEmissora.Titulo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0px;
  padding: 0px;
  background-color: rgba(255, 151, 34, 0.2);
  border-radius: 10px;
  width: 100%;
`

function getCargaHoraria(matricula?: MatriculaModel) {
  const cursoId = String(matricula?.curso?.id ?? '')
  const uf = matricula?.uf

  if (uf === 'DF' && cursoId === '1') return 45
  if (uf === 'SC' && cursoId === '1') return 45
  if (uf === 'DF' && cursoId === '11') return 13

  return matricula?.curso?.cargaHoraria
}

function DadosCurso() {
  const { Container } = DadosCurso
  const { matricula } = CertificadoStore.useState()
  const cargaHoraria = useMemo(() => getCargaHoraria(matricula), [matricula])
  return (
    <Container>
      <DescriptionList>
        <DescriptionItem label={<strong>Nome do Curso</strong>}>
          {matricula?.curso?.nome}
        </DescriptionItem>
        <DescriptionItem label={<strong>Carga Horária</strong>}>{cargaHoraria}</DescriptionItem>
        {matricula?.dataInicioModuloTeorico && matricula?.dataFimModuloTeorico && (
          <DescriptionItem label={<strong>Período</strong>}>
            {format(new Date(matricula?.dataInicioModuloTeorico), 'dd/MM/yyyy')} a{' '}
            {format(new Date(matricula?.dataFimModuloTeorico), 'dd/MM/yyyy')}
          </DescriptionItem>
        )}
        {matricula?.dataValidadeCurso && (
          <DescriptionItem label={<strong>Validade</strong>}>
            {format(new Date(matricula?.dataValidadeCurso), 'dd/MM/yyyy')}
          </DescriptionItem>
        )}
      </DescriptionList>
    </Container>
  )
}
DadosCurso.Container = styled.div`
  grid-area: ${DadosCurso_name};
`

export const RenderCertificado = () => {
  const { cursoId } = useParams<{ cursoId: string }>()
  console.log(cursoId)
  const [{ data, error, loading }] = useAxios(`/certificados/?cursoId=${cursoId}`)

  useEffect(() => {
    if (data?.identificacaoCertificado) {
      window.location.href = `${process.env.REACT_APP_API_URL}/certificados/render/${data?.identificacaoCertificado}`
    }
  }, [data?.identificacaoCertificado])

  if (loading) return <p>Gerando certificado</p>
  if (error)
    return <p style={{ textAlign: 'center', fontSize: 14 }}>{error?.response?.data.message}</p>
  return null
}
